import {
  existsSync,
  mkdirSync,
  readFileSync,
  writeFileSync,
  chmodSync,
} from 'node:fs';
import { dirname } from 'node:path';
import { maxPath } from './paths.js';

/**
 * Secure, file-based credential store.
 *
 * API keys are persisted to `~/.max/credentials` as dotenv-style `NAME=value`
 * lines with owner-only permissions (dir 0700, file 0600) — the same approach
 * used by tools like the GitHub and AWS CLIs. This replaces the previous behavior
 * of writing keys as plaintext into a `.env` file in the current working directory,
 * which scattered credentials (and leaked them via default-readable permissions)
 * whenever the CLI was launched from a different folder — e.g. under `npx`.
 */
const CREDENTIALS_FILE = maxPath('credentials');

const DIR_MODE = 0o700;
const FILE_MODE = 0o600;

/** Parse dotenv-style `NAME=value` lines into a map. Ignores blanks and comments. */
function parse(content: string): Map<string, string> {
  const map = new Map<string, string>();
  for (const rawLine of content.split('\n')) {
    const line = rawLine.trim();
    if (!line || line.startsWith('#') || !line.includes('=')) continue;
    const eq = line.indexOf('=');
    const key = line.slice(0, eq).trim();
    const value = line.slice(eq + 1).trim();
    if (key) map.set(key, value);
  }
  return map;
}

function serialize(map: Map<string, string>): string {
  const lines = ['# Max credentials — do not commit. Managed by the CLI.'];
  for (const [key, value] of map) {
    lines.push(`${key}=${value}`);
  }
  return lines.join('\n') + '\n';
}

function readAll(): Map<string, string> {
  if (!existsSync(CREDENTIALS_FILE)) return new Map();
  try {
    return parse(readFileSync(CREDENTIALS_FILE, 'utf-8'));
  } catch {
    return new Map();
  }
}

function writeAll(map: Map<string, string>): boolean {
  try {
    const dir = dirname(CREDENTIALS_FILE);
    if (!existsSync(dir)) {
      mkdirSync(dir, { recursive: true, mode: DIR_MODE });
    }
    writeFileSync(CREDENTIALS_FILE, serialize(map), { mode: FILE_MODE });
    // Enforce perms even if the file already existed with looser permissions.
    chmodSync(CREDENTIALS_FILE, FILE_MODE);
    return true;
  } catch {
    return false;
  }
}

/** Read a single stored key, or undefined if not present. */
export function getStoredKey(name: string): string | undefined {
  return readAll().get(name);
}

/** Persist (or overwrite) a single key with owner-only permissions. */
export function setStoredKey(name: string, value: string): boolean {
  const map = readAll();
  map.set(name, value);
  return writeAll(map);
}

/** Remove a stored key. Returns true if the store was updated (or key absent). */
export function deleteStoredKey(name: string): boolean {
  const map = readAll();
  if (!map.has(name)) return true;
  map.delete(name);
  return writeAll(map);
}

/** Names of all currently stored keys. */
export function listStoredKeyNames(): string[] {
  return Array.from(readAll().keys());
}

/**
 * Load stored keys into `process.env`, without overriding values already set.
 * Precedence: existing process env (shell / project `.env`) wins over the store.
 * Call once at startup, after dotenv has loaded.
 */
export function loadStoredKeysIntoEnv(): void {
  for (const [key, value] of readAll()) {
    if (process.env[key] === undefined || process.env[key] === '') {
      process.env[key] = value;
    }
  }
}
